import type { TFunction } from 'i18next';

const languageNameHelper = (lang: string) => new Intl.DisplayNames([lang], { type: 'language' });

const SUFFIXES = new Map([
  ['one', 'st'],
  ['two', 'nd'],
  ['few', 'rd'],
  ['other', 'th']
]);

// From https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/PluralRules/PluralRules
const formatOrdinals = (lang: string, number: number) => {
  const PLURAL_RULES = new Intl.PluralRules(lang, { type: 'ordinal' });

  // console.log(PLURAL_RULES.resolvedOptions());

  const rule = PLURAL_RULES.select(number);
  const suffix = SUFFIXES.get(rule);
  return `${number}${suffix}`;
};

const getVideogameKey = (videogame: VideogameSlug) => {
  if (videogame === 'dota-2') return 'dota2';
  if (videogame === 'league-of-legends') return 'lol';
  if (videogame === 'cs-go') return 'csgo';
  return videogame;
};

const localizedMapName = (
  gameMap: GameMap,
  t: TFunction<'translation', undefined, 'translation'>
): string => localizeVgResource('vg-csgo-map', gameMap.name, t);

const localizedHeroName = (
  hero: Hero | null,
  videogame: 'dota2' | 'lol',
  t: TFunction<'translation', undefined, 'translation'>
): string => {
  if (!hero) return '';

  const heroName = hero.localized_name || hero.name;

  const vg_key = videogame === 'dota2' ? 'dota2-hero' : 'lol-champion';
  return localizeVgResource(`vg-${vg_key}`, heroName, t);
};

const localizeVgResource = (
  resource_key: string,
  default_value: string,
  t: TFunction<'translation', undefined, 'translation'>
): string => {
  const translation_key = `${resource_key}-${default_value}`;
  const translation = t(translation_key);

  // if key is not handled yet (ie. new champion) or language not handled yet, default to EN name
  return translation === '' || translation === translation_key ? default_value : translation;
};

const localizeTournamentName = (t: TFunction, tournamentName: string): string => {
  const isGroupPhaseOrStage = tournamentName.match(/(phase|stage|group)\s+([a-z0-9])(?!\w)/gi);

  if (!isGroupPhaseOrStage) {
    const tournamentKey = tournamentName.toLowerCase().replace(/ /g, '_').replace(/-/g, '_');
    const translation = t(`tournament-${tournamentKey}`);
    if (translation !== '') {
      return translation;
    }
  }

  const tournamentWords = tournamentName.split(' ');
  const translation = tournamentWords
    .map(word => {
      const key = `tournament-${word.toLowerCase().replace(/-/g, '_')}`;
      return t(key, { defaultValue: word });
    })
    .join(' ')
    .replace(/_/g, ' ');

  return translation;
};

export {
  languageNameHelper,
  formatOrdinals,
  localizedMapName,
  localizedHeroName,
  getVideogameKey,
  localizeTournamentName
};
